/**
 * Tournaments list — flat table with inline folder tools.
 *
 * Same layout as before, with three additions:
 *   1. "New Folder" button in the header (opens an inline modal).
 *   2. Filter chip row above the table: All / Unfiled / one chip per
 *      user folder. Clicking a chip filters the tournaments query.
 *   3. Bulk-select gains a "Move to..." dropdown alongside the delete
 *      action; Folder column shows the current placement per row.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderPlus, FolderInput, MoreVertical, Pencil, Trash2, Loader2, X,
} from "lucide-react";

import ListTemplate from "../_templates/ListTemplate";
import BulkDeleteAction from "../../../components/common/BulkDeleteAction";
import {
  listTournaments, deleteTournament,
  listTournamentFolders, createTournamentFolder,
  renameTournamentFolder, deleteTournamentFolder,
  moveTournamentsToFolder,
} from "../../../api/tournament.api";
import useNationalities from "../../../hooks/useNationalities";
import useAuthStore from "../../../store/auth.store";

const TYPE_TONE = {
  INTERNATIONAL: "bg-button-primary/20 text-button-primary border-button-primary/40",
  DOMESTIC:      "bg-blue-500/15 text-blue-300 border-blue-400/30",
  LEAGUE:        "bg-amber-500/15 text-amber-300 border-amber-400/30",
  SCHOOL:        "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  CLUB:          "bg-purple-500/15 text-purple-300 border-purple-400/30",
  LOCAL:         "bg-rose-500/15 text-rose-300 border-rose-400/30",
};
const Pill = ({ value, tone }) =>
  value ? (
    <span className={`inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${tone || "bg-bg-secondary text-text-secondary border-border-default"}`}>
      {value}
    </span>
  ) : null;


export default function TournamentList() {
  const navigate = useNavigate();
  const { nameOf } = useNationalities();
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all"); // "all" | "none" | <uuid>
  const [foldersLoading, setFoldersLoading] = useState(true);
  // Bumped on every folder mutation (create/rename/delete/move) so the
  // ListTemplate refetches the tournament list without us having to hold
  // a ref to its internal load function.
  const [reloadSignal, setReloadSignal] = useState(0);

  const [showNewModal, setShowNewModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadFolders = useCallback(() => {
    setFoldersLoading(true);
    return listTournamentFolders()
      .then((r) => setFolders(r.data?.results || []))
      .catch(() => setFolders([]))
      .finally(() => setFoldersLoading(false));
  }, []);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  // Fetch function baked with the active-folder filter.
  const fetchFn = useMemo(() => {
    const folderParam =
      activeFolder === "all" ? undefined
      : activeFolder === "none" ? "none"
      : activeFolder;
    return (page = 1, opts = {}) =>
      listTournaments(page, { ...opts, folder: folderParam });
  }, [activeFolder]);

  const bumpReload = () => setReloadSignal((s) => s + 1);

  // Header slot — "+ New Folder" button.
  const headerActions = (
    <button
      type="button"
      onClick={() => setShowNewModal(true)}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-border-default bg-bg-secondary hover:bg-bg-secondary/60 text-sm font-semibold"
    >
      <FolderPlus className="w-4 h-4" />
      New Folder
    </button>
  );

  // Filter chip strip — All / Unfiled / user folders.
  const filterStrip = (
    <div className="flex flex-wrap items-center gap-1.5">
      <Chip
        active={activeFolder === "all"}
        onClick={() => setActiveFolder("all")}
      >
        All
      </Chip>
      <Chip
        active={activeFolder === "none"}
        onClick={() => setActiveFolder("none")}
      >
        Unfiled
      </Chip>
      <span className="mx-1 text-text-secondary/40">·</span>
      {foldersLoading && folders.length === 0 && (
        <span className="text-xs text-text-secondary flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> loading folders
        </span>
      )}
      {folders.map((f) => (
        <FolderChip
          key={f.id}
          folder={f}
          active={activeFolder === f.id}
          onClick={() => setActiveFolder(f.id)}
          onRename={() => setRenameTarget(f)}
          onDelete={() => setDeleteTarget(f)}
        />
      ))}
    </div>
  );

  // Bulk actions — move-to-folder + delete.
  const bulkActions = useCallback((ids, reset, reload) => (
    <div className="flex items-center gap-2">
      <MoveToFolderAction
        ids={ids}
        folders={folders}
        currentFolderId={activeFolder !== "all" && activeFolder !== "none" ? activeFolder : null}
        onDone={() => { reset(); reload(); bumpReload(); loadFolders(); }}
      />
      {isSuperuser && (
        <BulkDeleteAction
          ids={ids}
          deleteFn={deleteTournament}
          label="tournaments"
          reset={reset}
          reload={() => { reload(); loadFolders(); }}
        />
      )}
    </div>
  ), [folders, activeFolder, isSuperuser, loadFolders]);

  return (
    <>
      <ListTemplate
        title="Tournaments"
        fetchFn={fetchFn}
        deleteFn={deleteTournament}
        getAddPath={() => "/cricket/tournaments/add"}
        getEditPath={(id) => `/cricket/tournaments/${id}/edit`}
        extraHeaderActions={headerActions}
        filterStrip={filterStrip}
        reloadSignal={reloadSignal}
        bulkActions={bulkActions}
        columns={[
          { key: "name",            label: "Tournament", render: (r) => (
            <button
              onClick={() => navigate(`/cricket/tournaments/${r.id}`)}
              className="font-medium text-button-primary hover:underline text-left"
              title="View competitions in this tournament"
            >
              {r.name}
            </button>
          ) },
          { key: "short_name",      label: "Short" },
          { key: "tournament_type", label: "Type",     render: (r) => <Pill value={r.tournament_type} tone={TYPE_TONE[r.tournament_type]} /> },
          { key: "folder_name",     label: "Folder",   render: (r) => (
            r.folder_name
              ? <Pill value={r.folder_name} tone="bg-button-primary/10 text-button-primary border-button-primary/30" />
              : <span className="text-text-secondary/60 text-xs italic">Unfiled</span>
          )},
          { key: "nationality",     label: "Country",  render: (r) => nameOf(r.nationality) },
          { key: "established_year",label: "Est.",     render: (r) => r.established_year || "—" },
          { key: "is_active",       label: "Status",   render: (r) => (
            <span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${r.is_active ? "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" : "bg-bg-secondary text-text-secondary border-border-default"}`}>
              {r.is_active ? "Active" : "Inactive"}
            </span>
          )},
        ]}
      />

      {showNewModal && (
        <NewFolderModal
          onClose={() => setShowNewModal(false)}
          onCreated={(folder) => {
            setShowNewModal(false);
            loadFolders();
            if (folder?.id) setActiveFolder(folder.id);
          }}
        />
      )}
      {renameTarget && (
        <RenameFolderModal
          folder={renameTarget}
          onClose={() => setRenameTarget(null)}
          onDone={() => { setRenameTarget(null); loadFolders(); bumpReload(); }}
        />
      )}
      {deleteTarget && (
        <DeleteFolderModal
          folder={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => {
            const wasActive = activeFolder === deleteTarget.id;
            setDeleteTarget(null);
            loadFolders();
            if (wasActive) setActiveFolder("all");
            bumpReload();
          }}
        />
      )}
    </>
  );
}


function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-button-primary/20 text-button-primary border-button-primary/40 font-semibold"
          : "bg-bg-secondary/40 text-text-secondary border-border-default hover:text-text-primary hover:border-border-default/80"
      }`}
    >
      {children}
    </button>
  );
}


function FolderChip({ folder, active, onClick, onRename, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = () => setMenuOpen(false);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [menuOpen]);

  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        onClick={onClick}
        className={`text-xs pl-2.5 pr-1.5 py-1 rounded-l-full border-y border-l transition-colors ${
          active
            ? "bg-button-primary/20 text-button-primary border-button-primary/40 font-semibold"
            : "bg-bg-secondary/40 text-text-secondary border-border-default hover:text-text-primary"
        }`}
      >
        {folder.name}
        {typeof folder.tournament_count === "number" && (
          <span className="ml-1 text-[10px] opacity-70">({folder.tournament_count})</span>
        )}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
        className={`px-1 py-1 rounded-r-full border-y border-r transition-colors ${
          active
            ? "bg-button-primary/20 text-button-primary border-button-primary/40"
            : "bg-bg-secondary/40 text-text-secondary border-border-default hover:text-text-primary"
        }`}
        title="Folder actions"
        aria-label="Folder actions"
      >
        <MoreVertical className="w-3 h-3" />
      </button>
      {menuOpen && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute left-0 top-full mt-1 w-36 bg-bg-primary border border-border-default rounded-md shadow-xl py-1 z-30"
        >
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onRename(); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm hover:bg-bg-secondary/60"
          >
            <Pencil className="w-3.5 h-3.5" /> Rename
          </button>
          <button
            type="button"
            onClick={() => { setMenuOpen(false); onDelete(); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-rose-300 hover:bg-rose-500/10"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </span>
  );
}


// ─── Modal shell (the shared Modal is a confirm-dialog only) ──────────
function ModalShell({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-bg-primary border border-border-default rounded-xl p-5 shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold">{title}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-bg-secondary text-text-secondary">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


function NewFolderModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError("");
    createTournamentFolder(trimmed)
      .then((r) => onCreated(r?.data))
      .catch((err) => setError(err?.response?.data?.name?.[0] || err?.response?.data?.detail || err.message))
      .finally(() => setBusy(false));
  };
  return (
    <ModalShell onClose={onClose} title="New folder">
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-text-secondary text-xs uppercase tracking-widest">Folder name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. IPL"
            className="mt-1 w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-sm focus:outline-none focus:border-button-primary"
          />
        </label>
        {error && <div className="text-xs text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary">
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()} className="px-3 py-1.5 text-sm rounded-md bg-button-primary text-white hover:brightness-110 disabled:opacity-50">
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}


function RenameFolderModal({ folder, onClose, onDone }) {
  const [name, setName] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === folder.name) { onClose(); return; }
    setBusy(true);
    setError("");
    renameTournamentFolder(folder.id, trimmed)
      .then(onDone)
      .catch((err) => setError(err?.response?.data?.name?.[0] || err?.response?.data?.detail || err.message))
      .finally(() => setBusy(false));
  };
  return (
    <ModalShell onClose={onClose} title="Rename folder">
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-sm">
          <span className="text-text-secondary text-xs uppercase tracking-widest">Folder name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full bg-bg-secondary border border-border-default rounded-md px-3 py-2 text-sm focus:outline-none focus:border-button-primary"
          />
        </label>
        {error && <div className="text-xs text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="px-3 py-1.5 text-sm rounded-md bg-button-primary text-white hover:brightness-110 disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}


function DeleteFolderModal({ folder, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = () => {
    setBusy(true);
    setError("");
    deleteTournamentFolder(folder.id)
      .then(onDone)
      .catch((err) => setError(err?.response?.data?.detail || err.message))
      .finally(() => setBusy(false));
  };
  return (
    <ModalShell onClose={onClose} title="Delete folder">
      <div className="space-y-3 text-sm">
        <p>
          Delete folder <span className="font-semibold">{folder.name}</span>?
        </p>
        <p className="text-text-secondary text-xs">
          Tournaments inside move to <span className="font-medium">Unfiled</span>.
          The tournaments themselves are not deleted.
        </p>
        {error && <div className="text-xs text-rose-300">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-border-default hover:bg-bg-secondary">
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy} className="px-3 py-1.5 text-sm rounded-md bg-rose-500 text-white hover:brightness-110 disabled:opacity-50">
            {busy ? "Deleting…" : "Delete folder"}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


// Bulk "Move to folder" — dropdown of folders + Unfiled option.
function MoveToFolderAction({ ids, folders, currentFolderId, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const move = (folderId) => {
    if (folderId === currentFolderId) { setOpen(false); return; }
    setBusy(true);
    setError("");
    moveTournamentsToFolder(ids, folderId)
      .then(() => { setOpen(false); onDone(); })
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setBusy(false));
  };

  return (
    <div className="relative" onMouseDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-bg-secondary border border-border-default hover:bg-bg-secondary/60 disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
        Move to…
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-bg-primary border border-border-default rounded-md shadow-xl py-1 z-30 max-h-72 overflow-y-auto">
          <button
            type="button"
            onClick={() => move(null)}
            className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary/60 italic text-text-secondary"
          >
            (Unfiled)
          </button>
          {folders.length > 0 && <div className="h-px bg-border-default my-1" />}
          {folders.length === 0 && (
            <div className="px-3 py-2 text-xs text-text-secondary italic">
              No folders yet. Use "+ New Folder" first.
            </div>
          )}
          {folders.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => move(f.id)}
              disabled={f.id === currentFolderId}
              className="w-full text-left px-3 py-2 text-sm hover:bg-bg-secondary/60 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {f.name}
              {f.id === currentFolderId && <span className="text-[10px] text-text-secondary ml-1">(current)</span>}
            </button>
          ))}
          {error && <div className="px-3 py-2 text-xs text-rose-300">{error}</div>}
        </div>
      )}
    </div>
  );
}
